import { LogQueryRequest, LogQueryResponse, LogStats, ChartDataResponse, TopDataResponse, TimeFilter, ExecutionLockStatus } from '../types/LogTypes';
import config from '../config/config';
import { msalInstance } from '../index';
import { apiRequest } from '../authConfig';

const API_BASE_URL = config.apiBaseUrl;

class LogService {
  private async getAuthHeaders(): Promise<HeadersInit> {
    const accounts = msalInstance.getAllAccounts();
    
    if (accounts.length === 0) {
      throw new Error('No authenticated user found');
    }

    try {
      const response = await msalInstance.acquireTokenSilent({
        ...apiRequest,
        account: accounts[0],
      });

      return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${response.accessToken}`,
      };
    } catch (error) {
      // If silent token acquisition fails, try with popup
      const response = await msalInstance.acquireTokenPopup(apiRequest);
      
      return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${response.accessToken}`,
      };
    }
  }

  async queryLogs(request: LogQueryRequest): Promise<LogQueryResponse> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/logs/query`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error(`Failed to query logs: ${response.statusText}`);
    }

    return await response.json();
  }

  async getStats(): Promise<LogStats> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/logs/stats`, { headers });

    if (!response.ok) {
      throw new Error(`Failed to get stats: ${response.statusText}`);
    }

    return await response.json();
  }

  async getCustomers(): Promise<string[]> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/logs/customers`, { headers });

    if (!response.ok) {
      throw new Error(`Failed to get customers: ${response.statusText}`);
    }

    return await response.json();
  }

  async getServices(): Promise<string[]> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/logs/services`, { headers });

    if (!response.ok) {
      throw new Error(`Failed to get services: ${response.statusText}`);
    }

    return await response.json();
  }

  async getEnvironments(): Promise<string[]> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/logs/environments`, { headers });

    if (!response.ok) {
      throw new Error(`Failed to get environments: ${response.statusText}`);
    }

    return await response.json();
  }

  async getLogTypes(): Promise<string[]> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/logs/logtypes`, { headers });

    if (!response.ok) {
      throw new Error(`Failed to get log types: ${response.statusText}`);
    }

    return await response.json();
  }

  async getChartData(): Promise<ChartDataResponse> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/logs/chartdata`, { headers });

    if (!response.ok) {
      throw new Error(`Failed to get chart data: ${response.statusText}`);
    }

    return await response.json();
  }

  async getTopData(timeFilter: TimeFilter): Promise<TopDataResponse> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/logs/topdata?timeFilter=${timeFilter}`, { headers });

    if (!response.ok) {
      throw new Error(`Failed to get top data: ${response.statusText}`);
    }

    return await response.json();
  }

  async triggerAggregation(): Promise<{ message: string }> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/logs/trigger-aggregation`, {
      method: 'POST',
      headers,
    });

    if (!response.ok) {
      throw new Error(`Failed to trigger aggregation: ${response.statusText}`);
    }

    return await response.json();
  }

  async getExecutionLockStatus(): Promise<ExecutionLockStatus> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/logs/execution-lock-status`, { headers });

    if (!response.ok) {
      throw new Error(`Failed to get execution lock status: ${response.statusText}`);
    }

    return await response.json();
  }
}

const logService = new LogService();

export default logService;
